import {PIXI} from '@colonist/pixi'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {UIGameCardCountBadgeView} from '../ui_game/ui_game_card/UIGameCardCountBadgeView'
import {CardDataController, BaseCardData, CardClickPopupType, CardPopupInformation} from '@colonist/game-card-types'
import {FloatingText} from '../game_ui_view_objects/FloatingText'
import {UIPopupController} from '../ui_popup/UIPopupController'
import {UIGameManager} from '../ui_game/UIGameManager'
import {ViewAnimationController} from './ViewAnimationController'
import {SpriteView} from './SpriteView'
import {logError} from '../ui_utils/Utils'
import {UIGamePopupInformationView} from '../ui_game/ui_game_popup/UIGamePopupInformationView'
import {GameColor} from '../GameColor'
import {ArrayUtils} from '../../../../shared/utils/ArrayUtils'
import {TranslationObject} from '@colonist/i18n'
import {CA} from '../analytics/Analytics'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ActionsThatCanBeCancelled, GameAction} from '@colonist/game-base-types'
import {Sprites} from '../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../UIGameSpriteHandler'
import {ViewAnimationControllerAdapter} from './animation/ViewAnimationControllerAdapter'
import {UIGameAnimationBase} from './animation/UIGameAnimationBase'
import {CardEnum, CardTypeEnum, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export class CardView extends PIXI.Container {
    private readonly uiGameManager: UIGameManager
    private readonly cardBadgeView: UIGameCardCountBadgeView

    animationController: ViewAnimationController
    readonly view: SpriteView

    cardData: BaseCardData
    private currentCardCount: number
    private readonly forceCardBadge: boolean

    private currentAnimation: UIGameAnimationBase

    constructor(uiGameManager: UIGameManager, currentCardCount: number, x: number, y: number, w: number, h: number, cardEnum: CardEnum, container: PIXI.Container, setTip: boolean = false, forceCardBadge: boolean = false) {
        super()
        this.x = x
        this.y = y
        this.width = w
        this.height = h
        container.addChild(this)

        this.uiGameManager = uiGameManager
        this.animationController = new ViewAnimationControllerAdapter(this)
        this.currentCardCount = currentCardCount
        this.forceCardBadge = forceCardBadge

        const cardData = CardDataController.getCardDataForCard(cardEnum)
        const texture = CardView.getTextureForCard(cardEnum)
        this.view = new SpriteView(0, 0, w, h, this, texture)
        if(cardData == undefined) return
        this.cardData = cardData

        // Mar 1, 2021. Jeff. This is to make sure cards stack nicely in small screens: https://prntscr.com/10aulvn
        const totalNumberOfCards = Object.keys(CardEnum).length / 2
        const layerCardsByReverseSortingOrder = -this.cardData.cardEnum + totalNumberOfCards
        this.zIndex = layerCardsByReverseSortingOrder
        this.cardBadgeView = new UIGameCardCountBadgeView(currentCardCount, this)
        this.hideBadge()
        if(this.doesCardHaveBadge() || forceCardBadge) this.showBadge()

        if(setTip) this.setTip()
    }

    private doesCardHaveBadge(): boolean {
        if(this.forceCardBadge) return true

        return CardView.getIfCardHasBadgeFromType(this.cardData.cardTypeEnum, this.currentCardCount)
    }

    static getIfCardHasBadgeFromType(cardType: CardTypeEnum, cardCount: number): boolean {
        switch(cardType) {
            case CardTypeEnum.Resource:
            case CardTypeEnum.Commodity: return true
            case CardTypeEnum.ProgressTrade:
            case CardTypeEnum.ProgressPolitics:
            case CardTypeEnum.ProgressScience:
            case CardTypeEnum.Development: return cardCount > 1
            case CardTypeEnum.AnyResource: return false
        }
    }

    updateBadge(cardCount: number) {
        if(this.cardBadgeView == undefined) return

        this.currentCardCount = cardCount
        this.doesCardHaveBadge() ? this.showBadge() : this.hideBadge()

        this.cardBadgeView.updateText(cardCount)
    }

    updateTexture(texture: PIXI.Texture) {
        if(texture == this.view.texture) return
        this.view.texture = texture
    }

    hideBadge() {
        this.cardBadgeView.hideBadge()
    }

    showBadge() {
        this.cardBadgeView.showBadge()
    }

    showBadgeForDefaultCards() {
        this.showBadge()
        if(this.doesCardHaveBadge()) return
        this.hideBadge()
    }

    private setTip() {
        const {tipTitle, tipBody} = this.cardData
        this.view.setTip(tipTitle, [tipBody])
    }

    canCardExpand(): boolean {
        if(this.cardData.cardTypeEnum === CardTypeEnum.ProgressPolitics) return true
        if(this.cardData.cardTypeEnum === CardTypeEnum.ProgressScience) return true
        if(this.cardData.cardTypeEnum === CardTypeEnum.ProgressTrade) return true
        return false
    }

    bringCardToFrontOfParent() {
        if(this.parent == undefined) return
        const parent = this.parent
        parent.removeChild(this)
        parent.addChild(this)
    }

    activateInventoryCard() {
        this.view.setClickAction(() => {
            this.tappedPlayerCard()
        })
        this.view.setRightClickAction(() => {
            this.tappedPlayerCard(true)
        })
    }

    showTradeWindow(bankTrade: boolean = false) {
        const offeredResources = [this.cardData.cardEnum]
        this.uiGameManager.tradeController.onClickTrade(this.view, offeredResources, [], bankTrade)

        if(!UIUtilsEjsVariables.isUserOnMobileApp) return
        CA.sendEventSummed(CA.Category.Game, CA.Label.ClickedGameCardForTrade, UIGameEventEnums.GameEnd)
    }

    tappedPlayerCard(rightClick: boolean = false) {
        if(!this.canClickCard()) return
        this.uiGameManager.toolTipController.closeTip()
        switch(this.cardData.clickPopupType) {
            case CardClickPopupType.None: break
            case CardClickPopupType.NormalClick: this.clickedDevelopmentCard(); break
            case CardClickPopupType.TradeWindow: this.showTradeWindow(rightClick); break
            case CardClickPopupType.InformationPopup: this.createInformationPopup(); break
            case CardClickPopupType.ConfirmationPopup: this.createCardConfirmationPopup(); break
        }
    }

    canClickCard(): boolean {
        if(this.uiGameManager.gameController.isReplay) {
            new FloatingText({key: 'strings:game.errors.replayMode'}, this)
            return false
        }
        if(!this.uiGameManager.gameController.isMyTurn) {
            new FloatingText({key: 'strings:game.errors.notYourTurn'}, this)
            return false
        }
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase) {
            const doesClickingCardTriggerTrade = this.cardData.clickPopupType == CardClickPopupType.TradeWindow
            const devCardsDisabledErrorMessage: TranslationObject = {
                key: 'strings:game.errors.developmentCardsDisabled',
                options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
            }
            const errorMessage: TranslationObject = doesClickingCardTriggerTrade ? {key: 'strings:game.errors.tradingDisabled'} : devCardsDisabledErrorMessage
            new FloatingText(errorMessage, this)
            return false
        }
        if(!ArrayUtils.elementInArray(ActionsThatCanBeCancelled.actions, this.uiGameManager.gameController.currentState.actionState)) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, this)
            return false
        }
        if(!this.isValidTurnState()) {
            // July 18, 2021. Jeff: This was hacked together and is terrible code but just wanted to have this fixed for better UX
            const isCardAchemist = this.cardData.cardEnum == CardEnum.ProgressCardScienceAlchemist
            const errorMessage: TranslationObject = isCardAchemist ? {key: 'strings:game.errors.canOnlyBePlayedBeforeRollingDice'} : {key: 'strings:game.errors.finishCurrentAction'}
            new FloatingText(errorMessage, this)
            return false
        }
        return true
    }

    fadeOutCard() {
        this.view.tint = GameColor.getColorHexValue(GameColor.Color.CardViewFadedTint)
        this.cardBadgeView.fadeOutBadge()
    }

    fadeInCard() {
        this.view.tint = GameColor.getColorHexValue(GameColor.Color.White)
        this.cardBadgeView.fadeInBadge()
    }

    private isValidTurnState(): boolean {
        if(this.cardData.allowableTurnStates == undefined) return true
        if(ArrayUtils.elementInArray(this.cardData.allowableTurnStates, this.uiGameManager.gameController.currentState.turnState)) return true

        return false
    }

    private clickedDevelopmentCard() {
        this.uiGameManager.socketGameSend.clickedDevelopmentCard(this.cardData.cardEnum)
    }

    private createInformationPopup() {
        const {tipTitle, popupBody} = this.cardData
        UIPopupController.createSimplePopup(tipTitle, popupBody)
    }

    static getTextureForCard(cardEnum: CardEnum): PIXI.Texture {
        const cardData = CardDataController.getCardDataForCard(cardEnum)
        if(cardData == undefined) {
            logError('getTextureForCard', [cardEnum])
            return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.card_rescardback)
        }

        return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites[cardData.imageFileName])
    }

    private createCardConfirmationPopup() {
        this.uiGameManager.socketGameSend.requestActionSwap(GameAction.ClickedDevelopmentCard)
        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(this.cardData)
        const cardConfirmationPopupFormat: CardPopupInformation = {
            cardEnum: this.cardData.cardEnum,
            popupInformation: popupInformation,
        }

        this.uiGameManager.eventController.sendEvent(cardConfirmationPopupFormat, UIGameEventEnums.ConfirmUseDevelopmentCard)
    }

    setAnimation(animation: UIGameAnimationBase) {
        if(this.currentAnimation != undefined) this.currentAnimation.stop()
        this.currentAnimation = animation
        this.currentAnimation.start()
    }

    stopAnimation() {
        if(this.currentAnimation == undefined) return
        this.currentAnimation.stop()
    }
}
